import { BreakpointObserver } from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import EditorJS from '@editorjs/editorjs';
import { SearchService } from '@journal/core/api/search/search.service';
import { BREAKPOINTS } from '@sparro/app/core/config/constants.config';
import { ToastrService } from '@sparro/app/core/helpers/toastr/toastr.service';
import { Blog } from '@sparro/app/core/models/blog.model';
import { User } from '@sparro/app/core/models/user.model';
import { environment } from '@sparro/env/environment';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Table from '@editorjs/table';
// import SimpleImage from '@editorjs/simple-image';
import LinkTool from '@editorjs/link';
import Quote from '@editorjs/quote';
import Checklist from '@editorjs/checklist';
import Embed from '@editorjs/embed';
import ImageTool from '@editorjs/image';
import Delimiter from '@editorjs/delimiter';
import Warning from '@editorjs/warning';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';

@Component({
  selector: 'uni-blog-panel',
  templateUrl: './blog-panel.component.html',
  styleUrls: ['./blog-panel.component.scss'],
  providers: [DatePipe]
})
export class BlogPanelComponent implements OnInit {
  @Input() blog: Blog;
  @Input() self: boolean;
  @Input() source: string;
  @Input() user: User;
  @Input() blogCopyLink: string;
  @Output() metaSectionTop = new EventEmitter();
  competitionUrl: string = environment.competitionUrl;
  link: string;
  mobileView: boolean = false;
  content: any;
  sharedContent: any;
  showFloatingShare: boolean = false;

  @ViewChild('shareContainer') container: ElementRef;
  @ViewChild('editor') editorElement: ElementRef;

  public editor;

  constructor(
    public router: Router,
    private searchService: SearchService,
    public toast: ToastrService,
    public breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.link = `${environment.postUrl}${this.blog.slug}/`;
    this.initializeBreakpoints();
    if (this.blog) {
      this.content = this.blog.content;
      if (this.blog.body) {
        setTimeout(() => {
          this.initEditor();
        }, 1000);
      }
      this.formatContent();
      this.sharedContent = {
        title: this.blog.title,
        subtitle: this.blog.tagline,
        cover_image: this.blog.image
      };
    }
  }

  ngAfterViewInit(): void {
    if (this.container) {
      this.metaSectionTop.emit(this.container.nativeElement.offsetTop);
    }
  }

  initializeBreakpoints(): void {
    this.breakpointObserver.observe([BREAKPOINTS.below.md]).subscribe(result => {
      // console.log(result);
      if (result.breakpoints[BREAKPOINTS.below.md] != this.mobileView) {
        this.mobileView = !this.mobileView;
        this.formatContent();
      }
    });
  }

  initEditor(): void {
    this.editor = new EditorJS({
      holder: this.editorElement.nativeElement,
      readOnly: true,
      tools: {
        header: {
          class: Header
        },
        image: {
          class: ImageTool
        },
        list: {
          class: List
        },
        checklist: {
          class: Checklist,
          inlineToolbar: true
        },
        quote: {
          class: Quote
        },
        warning: Warning,
        marker: {
          class: Marker
        },
        delimiter: Delimiter,
        inlineCode: {
          class: InlineCode,
          shortcut: 'CMD+SHIFT+C'
        },
        linkTool: LinkTool,
        embed: {
          class: Embed,
          inlineToolbar: true
        },
        table: {
          class: Table
        }
      },
      // readOnly: true,
      data: this.blog.body ? JSON.parse(this.blog.body) : {}
    });
  }

  formatContent(): void {
    this.content = this.blog.content;
    // for <p>
    let textStyle = this.mobileView
      ? `<p style="font-size: 14px; line-height: 22px;">`
      : `<p style="font-size: 20px; line-height: 28px;">`;
    this.content = this.content.replaceAll('<p>', textStyle);

    // for h1
    textStyle = !this.mobileView
      ? `<h1 style="font-size: 2rem; font-weight: 700; line-height: 2.3rem;">`
      : `<h1 style="font-size: 24px; font-weight: 700; line-height: 28.1px;">`;
    this.content = this.content.replaceAll('<h1>', textStyle);

    // for h2
    textStyle = !this.mobileView
      ? `<h2 style="font-size: 1.75rem; font-weight: 700; line-height: 2rem;">`
      : `<h2 style="font-size: 24px; font-weight: 500; line-height: 28p.1px;">`;
    this.content = this.content.replaceAll('<h2>', textStyle);

    // for h3
    textStyle = !this.mobileView
      ? `<h3 style="font-size: 1.75rem; font-weight: 500; line-height: 2rem;">`
      : `<h3 style="font-size: 20px; font-weight: 500; line-height: 23.4px;">`;
    this.content = this.content.replaceAll('<h3>', textStyle);

    // for h4
    textStyle = !this.mobileView
      ? `<h4 style="font-size: 24px; font-weight: 500; line-height: 30px;">`
      : `<h4 style="font-size: 16px; font-weight: 500; line-height: 20px;">`;
    this.content = this.content.replaceAll('<h4>', textStyle);
  }

  goToDisciplineSearch(discipline): void {
    this.searchService.setSearchDiscipline(discipline);
    this.router.navigate(['search']);
  }

  getDisciplineSearchUrl(discipline): string {
    return `${environment.postUrl}search?disciplines=${discipline.id}`;
  }

  openUrl(url: string): void {
    if (!url.startsWith('http')) {
      url = `https://${url}`;
    }
    window.open(url, '_blank');
  }

  goToTopic(slug: string): string {
    return `${environment.topicUrl}${slug}`;
  }

  getProfileUrl(blog: Blog): string {
    const url = `${environment.profileUrl}${blog.author.username}/`;
    return url;
  }
}
